import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Filter, RotateCcw, ShoppingCart, X } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { fmtNum, fmtRub, fmtCpv, fmtPct, fmtDate } from "@/shared/lib/format";
import { CartPickerModal, Toast } from "@/shared/ui";
import { useBloggers, useCampaigns, useAddBloggerToCampaign, type Blogger } from "@/shared/api/wb";
import { useDraftStore } from "@/shared/store/draft";
import { BloggerCard, type CartSelection } from "@/features/blogger-card";
import {
  DEFAULT_FILTERS,
  PLATFORMS_META,
  applyFilters,
  countActiveFilters,
  deserializeFilters,
  facetCounts,
  serializeFilters,
  sortBloggers,
  sortNiches,
  catLabel,
  type FilterState,
  type SortKey,
} from "./Catalog/engine";
import { FilterSidebar } from "./Catalog/FilterSidebar";

export function CatalogPage() {
  const location = useLocation();
  const navigate = useNavigate();
  // Captured once on mount: the filters-sync effect below does a `replace`
  // navigation on first render (to serialize filters into the URL) which
  // drops location.state, so reading it live would lose the campaign we
  // came from before the user ever opens the picker.
  const [preselectedCampaignId] = useState(
    () => (location.state as Record<string, string> | null)?.preselectedCampaignId,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<FilterState>(() => deserializeFilters(searchParams));
  const [sort, setSort] = useState<SortKey>("relevance");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobilePending, setMobilePending] = useState<FilterState>(DEFAULT_FILTERS);
  const [history, setHistory] = useState<FilterState[]>([]);

  const { data: allBloggers = [], isLoading } = useBloggers({});
  const { data: campaignsList = [] } = useCampaigns();
  const { draft, addBloggerFormat, removeBloggerFormat, assignCreative } = useDraftStore();
  const addToCampaign = useAddBloggerToCampaign();

  const [pending, setPending] = useState<{ blogger: Blogger; sel: CartSelection } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [toastCampaignId, setToastCampaignId] = useState<string | null>(null);

  const commitFilters = useCallback((next: FilterState) => {
    setHistory(h => [...h.slice(-9), filters]);
    setFilters(next);
  }, [filters]);

  const resetAll = useCallback(() => {
    setHistory(h => [...h.slice(-9), filters]);
    setFilters(DEFAULT_FILTERS);
  }, [filters]);

  useEffect(() => {
    setSearchParams(serializeFilters(filters), { replace: true });
  }, [filters, setSearchParams]);

  const openMobile = () => { setMobilePending(filters); setMobileOpen(true); };
  const applyMobile = () => { commitFilters(mobilePending); setMobileOpen(false); };
  const closeMobile = () => setMobileOpen(false);

  const filtered = useMemo(
    () => sortBloggers(applyFilters(allBloggers, filters), sort),
    [allBloggers, filters, sort],
  );

  const mobilePreviewCount = useMemo(
    () => applyFilters(allBloggers, mobilePending).length,
    [allBloggers, mobilePending],
  );

  const availableNiches = useMemo(
    () => sortNiches([...new Set(allBloggers.flatMap(b => b.cats))]),
    [allBloggers],
  );

  const platformCounts = useMemo(
    () => facetCounts(allBloggers, filters, 'platform', PLATFORMS_META.map(p => p.id)),
    [allBloggers, filters],
  );
  const nicheCounts = useMemo(
    () => facetCounts(allBloggers, filters, 'niche', availableNiches),
    [allBloggers, filters, availableNiches],
  );

  const multiPlatformWarning = (filters.platform.length !== 1) && (filters.cpvMin !== null || filters.cpvMax !== null);
  const activeCount = useMemo(() => countActiveFilters(filters), [filters]);

  const cartCount = useMemo(
    () => Object.values(draft.bloggerFormats).filter(sels => sels && sels.length > 0).length,
    [draft.bloggerFormats],
  );

  const sidebarProps = {
    filters, onChange: commitFilters,
    platformCounts, nicheCounts, availableNiches,
    multiPlatformWarning, onReset: resetAll,
  };

  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-[1360px] mx-auto px-4 pt-6 pb-14 flex gap-6 items-start">

        {/* Desktop sidebar */}
        <aside className="hidden lg:flex flex-col w-[272px] shrink-0 sticky top-4" style={{ height: 'calc(100vh - 60px - 32px)' }}>
          <FilterSidebar {...sidebarProps} />
        </aside>

        {/* Results */}
        <main className="flex-1 min-w-0">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h1 className="font-display font-extrabold text-[28px] tracking-tight mb-0.5">Каталог блогеров</h1>
              <p className="text-ink-60 text-[13.5px]">
                {isLoading ? 'Загрузка…' : `В базе ${allBloggers.length}`}
              </p>
            </div>
            {cartCount > 0 && (
              <Link
                to="/campaign/new/bloggers"
                className="shrink-0 inline-flex items-center gap-2 h-10 px-4 rounded-pill bg-lime text-[#1A3A05] font-display font-bold text-[13.5px] hover:bg-lime/90 transition-colors shadow-sm"
              >
                <ShoppingCart size={15} />
                В РК
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#1A3A05] text-lime text-[11px] font-mono">
                  {cartCount}
                </span>
              </Link>
            )}
          </div>

          {/* Mobile search + filter button */}
          <div className="lg:hidden flex gap-2 mb-3">
            <input
              placeholder="Поиск…"
              value={filters.search}
              onChange={e => commitFilters({ ...filters, search: e.target.value })}
              className="flex-1 h-10 px-3 rounded-md border border-ink-12 bg-white text-[13px] outline-none focus:border-ink"
            />
            <button onClick={openMobile}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md border-2 border-ink bg-ink text-white text-[13px] font-display font-semibold">
              <Filter size={14} />
              Фильтры
              {activeCount > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white text-ink text-[11px] font-mono">{activeCount}</span>
              )}
            </button>
          </div>

          {/* Active filter chips */}
          <ActiveFilterChips filters={filters} onChange={commitFilters} onResetAll={resetAll} />

          {/* Toolbar */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-[13px] text-ink-40">
              {isLoading ? '…' : filtered.length === allBloggers.length
                ? `${filtered.length} блогеров`
                : `Найдено ${filtered.length}`}
            </div>
            <SortBar sort={sort} onChange={setSort} />
          </div>

          {/* Results */}
          {isLoading ? (
            <div className="py-20 text-center text-ink-40 text-[14px]">Загрузка блогеров…</div>
          ) : filtered.length === 0 ? (
            <EmptyResults canUndo={history.length > 0} onUndo={() => {
              const prev = history[history.length - 1];
              setHistory(h => h.slice(0, -1));
              setFilters(prev);
            }} />
          ) : (
            <div className="flex flex-col gap-2">
              {filtered.map(b => {
                const isInCart = (draft.bloggerFormats[b.id]?.length ?? 0) > 0;
                return (
                  <BloggerCard
                    key={b.id}
                    blogger={b}
                    context="catalog"
                    isInCart={isInCart}
                    cartSelections={draft.bloggerFormats[b.id]}
                    onCartAdd={sel => setPending({ blogger: b, sel })}
                    onCartRemove={sel => removeBloggerFormat(b.id, sel.platform, sel.type)}
                    seedingLocked={false}
                    nativeLocked={false}
                  />
                );
              })}
            </div>
          )}
        </main>
      </div>

      {/* Cart picker modal */}
      {pending && (
        <CartPickerModal
          blogger={pending.blogger}
          cartSelection={pending.sel}
          preselectedCampaignId={preselectedCampaignId}
          onConfirm={(variantId, campaignId, date) => {
            const { blogger, sel } = pending;
            if (campaignId === "draft") {
              addBloggerFormat(blogger.id, sel);
              if (variantId) assignCreative(blogger.id, variantId);
              setPending(null);
              setToastCampaignId(null);
              setToast(`${blogger.name} добавлен в черновик РК`);
              return;
            }
            // Existing, already-launched campaign — this must actually reach
            // the campaign (same /expand call the in-campaign "Добавить
            // каналы" flow uses), not just the local draft cart, otherwise
            // the channel silently vanishes and nothing gets billed.
            const campaignName = campaignsList.find(c => c.id === campaignId)?.product ?? "кампанию";
            addToCampaign.mutate(
              {
                campaignId,
                config: {
                  blogger_id: blogger.id,
                  placement_type: sel.type,
                  format_id: sel.formatId || undefined,
                  format_name: sel.formatName || undefined,
                  variant_id: variantId ?? undefined,
                  date,
                },
              },
              {
                onSuccess: () => {
                  setPending(null);
                  setToastCampaignId(campaignId);
                  setToast(`${blogger.name} добавлен в «${campaignName}» на ${date ? fmtDate(date) : "—"} · ${fmtRub(sel.price)} спишется после выхода`);
                },
              },
            );
          }}
          onClose={() => setPending(null)}
        />
      )}

      {toast && (
        <Toast
          message={toast}
          onHide={() => { setToast(null); setToastCampaignId(null); }}
          action={toastCampaignId ? { label: "Перейти в РК", onClick: () => navigate(`/campaign/${toastCampaignId}`) } : undefined}
        />
      )}

      {/* Mobile bottom-sheet */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={closeMobile} />
          <div className="fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-2xl bg-white shadow-2xl"
            style={{ maxHeight: '90vh' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-ink-12 shrink-0">
              <span className="font-display font-bold text-[16px]">Фильтры</span>
              <button onClick={closeMobile} className="text-ink-40 hover:text-ink"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <FilterSidebar
                filters={mobilePending} onChange={setMobilePending}
                platformCounts={facetCounts(allBloggers, mobilePending, 'platform', PLATFORMS_META.map(p => p.id))}
                nicheCounts={facetCounts(allBloggers, mobilePending, 'niche', availableNiches)}
                availableNiches={availableNiches}
                multiPlatformWarning={(mobilePending.platform.length !== 1) && (mobilePending.cpvMin !== null || mobilePending.cpvMax !== null)}
                onReset={() => setMobilePending(DEFAULT_FILTERS)} />
            </div>
            <div className="p-4 border-t border-ink-12 shrink-0">
              <button onClick={applyMobile}
                className="w-full h-12 rounded-pill bg-ink text-white font-display font-bold text-[15px]">
                Показать {mobilePreviewCount} блогеров
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Active filter chips ──────────────────────────────────────────────────────

export function ActiveFilterChips({ filters, onChange, onResetAll }: {
  filters: FilterState;
  onChange: (f: FilterState) => void;
  onResetAll: () => void;
}) {
  const chips = getChips(filters, onChange);
  if (!chips.length) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap mb-3">
      {chips.map(c => (
        <button key={c.id} onClick={c.onRemove}
          className="inline-flex items-center gap-1.5 h-7 pl-3 pr-2 rounded-pill bg-ink text-white text-[12px] font-display font-semibold hover:bg-[#333]">
          {c.label}
          <X size={12} strokeWidth={2.5} />
        </button>
      ))}
      <button onClick={onResetAll}
        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-pill border border-ink-12 text-[12px] text-ink-40 hover:text-ink font-display font-semibold">
        <RotateCcw size={11} /> Сбросить всё
      </button>
    </div>
  );
}

type Chip = { id: string; label: string; onRemove: () => void };

function getChips(f: FilterState, set: (f: FilterState) => void): Chip[] {
  const d = DEFAULT_FILTERS;
  const chips: Chip[] = [];
  const rm = <K extends keyof FilterState>(k: K) => set({ ...f, [k]: d[k] });
  const rm2 = <K extends keyof FilterState>(k1: K, k2: K) => set({ ...f, [k1]: d[k1], [k2]: d[k2] });

  if (f.platform.length) chips.push({ id: 'platform', label: f.platform.join(', '), onRemove: () => rm('platform') });
  if (f.reachMin !== null || f.reachMax !== null) chips.push({
    id: 'reach', onRemove: () => rm2('reachMin', 'reachMax'),
    label: `Охват: ${rangeLabel(f.reachMin, f.reachMax, fmtNum)}`,
  });
  if (f.priceMin !== null || f.priceMax !== null) chips.push({
    id: 'price', onRemove: () => rm2('priceMin', 'priceMax'),
    label: `Цена: ${rangeLabel(f.priceMin, f.priceMax, fmtRub)}`,
  });
  if (f.hidePriceNull) chips.push({ id: 'hideNull', label: 'Без цены скрыты', onRemove: () => rm('hidePriceNull') });
  if (f.cpvMin !== null || f.cpvMax !== null) chips.push({
    id: 'cpv', onRemove: () => rm2('cpvMin', 'cpvMax'),
    label: `CPV: ${rangeLabel(f.cpvMin, f.cpvMax, fmtCpv)}`,
  });
  if (f.budgetFit !== null) chips.push({ id: 'budget', label: `Бюджет: ${fmtRub(f.budgetFit)}`, onRemove: () => rm('budgetFit') });
  if (f.erMin !== null || f.erMax !== null) chips.push({
    id: 'er', onRemove: () => rm2('erMin', 'erMax'),
    label: `ER: ${rangeLabel(f.erMin, f.erMax, v => fmtPct(v, 0))}`,
  });
  if (f.scoreMin !== null) chips.push({ id: 'score', label: `Качество ≥ ${f.scoreMin}`, onRemove: () => rm('scoreMin') });
  if (f.search) chips.push({ id: 'search', label: `«${f.search}»`, onRemove: () => rm('search') });
  if (f.niche.length) chips.push({ id: 'niche', label: `Категории: ${f.niche.map(catLabel).join(', ')}`, onRemove: () => rm('niche') });
  if (f.excludeNiche.length) chips.push({ id: 'xniche', label: `Исключить: ${f.excludeNiche.map(catLabel).join(', ')}`, onRemove: () => rm('excludeNiche') });

  return chips;
}

function rangeLabel(min: number | null, max: number | null, fmt: (n: number) => string): string {
  if (min !== null && max !== null) return `${fmt(min)} — ${fmt(max)}`;
  if (min !== null) return `от ${fmt(min)}`;
  if (max !== null) return `до ${fmt(max)}`;
  return '';
}

// ─── Sort bar ─────────────────────────────────────────────────────────────────

type SortField = 'reach' | 'price' | 'cpv' | 'score';

const SORT_FIELDS: { field: SortField; label: string; defaultDir: 'asc' | 'desc' }[] = [
  { field: 'reach',  label: 'Охват',    defaultDir: 'desc' },
  { field: 'price',  label: 'Цена',     defaultDir: 'asc'  },
  { field: 'cpv',    label: 'CPV',      defaultDir: 'asc'  },
  { field: 'score',  label: 'Качество', defaultDir: 'desc' },
];

export function SortBar({ sort, onChange }: { sort: SortKey; onChange: (s: SortKey) => void }) {
  const activeField = sort === 'relevance' ? null : (sort.split('_')[0] as SortField);
  const activeDir   = sort === 'relevance' ? null : (sort.split('_')[1] as 'asc' | 'desc');

  function handleClick(field: SortField, defaultDir: 'asc' | 'desc') {
    if (activeField === field) {
      onChange(`${field}_${activeDir === 'asc' ? 'desc' : 'asc'}` as SortKey);
    } else {
      onChange(`${field}_${defaultDir}` as SortKey);
    }
  }

  return (
    <div className="inline-flex items-center gap-0.5 p-0.5 rounded-pill bg-ink-6">
      <button
        onClick={() => onChange('relevance')}
        className={cn(
          'h-7 px-3 rounded-pill text-[12px] font-display font-semibold transition-colors',
          sort === 'relevance' ? 'bg-white text-ink shadow-sm' : 'text-ink-60 hover:text-ink',
        )}
      >
        Релевантность
      </button>
      {SORT_FIELDS.map(({ field, label, defaultDir }) => {
        const active = activeField === field;
        const dir = active ? activeDir : defaultDir;
        return (
          <button
            key={field}
            onClick={() => handleClick(field, defaultDir)}
            className={cn(
              'h-7 px-3 rounded-pill text-[12px] font-display font-semibold transition-colors inline-flex items-center gap-1',
              active ? 'bg-white text-ink shadow-sm' : 'text-ink-60 hover:text-ink',
            )}
          >
            {label}
            {active && <span className="text-[10px] leading-none">{dir === 'asc' ? '↑' : '↓'}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

export function EmptyResults({ canUndo, onUndo }: { canUndo: boolean; onUndo: () => void }) {
  return (
    <div className="py-20 flex flex-col items-center gap-4 text-center">
      <div className="w-16 h-16 rounded-full bg-ink-6 flex items-center justify-center">
        <Filter size={24} className="text-ink-40" />
      </div>
      <div>
        <p className="font-display font-bold text-[17px] mb-1">Никто не найден</p>
        <p className="text-ink-40 text-[13.5px]">Попробуйте снять один из фильтров</p>
      </div>
      {canUndo && (
        <button onClick={onUndo}
          className={cn(
            "inline-flex items-center gap-2 h-9 px-4 rounded-pill border-2 border-ink",
            "text-[13px] font-display font-semibold hover:bg-ink hover:text-white transition-colors",
          )}>
          <RotateCcw size={14} /> Отменить последний фильтр
        </button>
      )}
    </div>
  );
}
